// ./src/pages/private/EmbeddingEventTemplatesPage/EmbeddingEventTemplatesPage.tsx
import React, { useState, useCallback } from "react";
import { Stack } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
} from "@/components/generic";
import {
  EmbeddingEventTemplateFormModal,
  EmbeddingEventTemplatesTable,
} from "@/components/features";
import { useEmbeddingEventTemplates } from "@/hooks";
import { EmbeddingEventTemplate } from "@/utils/api";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE } from "@/config";

export const EmbeddingEventTemplatesPage: React.FC<PageProps> = (
  props: PageProps
) => {
  const { isAuthenticated } = useApp();

  // Use the templates hook for all data management
  const {
    // Data state
    embeddingEventTemplate,
    embeddingEventTemplates,
    loading,
    loadingMore,
    hasMore,
    isEmpty,

    // Search and filters
    sortBy,

    // Operations state
    isCreating,
    isUpdating,
    removingTemplateId,
    selectedTemplate,

    // Data operations
    list,
    loadMore,
    refresh,

    // Search and filter operations
    setFilters,
    setSortBy,

    // CRUD operations
    create,
    update,
    remove,

    // UI state management
    setSelectedTemplate,

    // Error handling
    lastError,
    clearError,
  } = useEmbeddingEventTemplates({
    autoLoad: true,
    isAuthenticated,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Local UI state
  const [formModalOpened, setFormModalOpened] = useState<boolean>(false);
  const [templateToDelete, setTemplateToDelete] =
    useState<EmbeddingEventTemplate | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");

  // Handle search input change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);

      // Apply search filter
      const newFilters: any = {};
      if (value.trim()) {
        newFilters.searchKeyword = value.trim();
      }

      // Update filters and reload data
      setFilters(newFilters);
      list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    },
    [sortBy, setFilters, list]
  );

  // Handle add template button click
  const handleAddTemplate = useCallback(() => {
    setSelectedTemplate(null);
    setFormModalOpened(true);
  }, [setSelectedTemplate]);

  // Handle edit template
  const handleEditTemplate = useCallback(
    (template: EmbeddingEventTemplate) => {
      setSelectedTemplate(template);
      setFormModalOpened(true);
    },
    [setSelectedTemplate]
  );

  // Handle duplicate template
  const handleDuplicateTemplate = useCallback(
    (template: EmbeddingEventTemplate) => {
      setSelectedTemplate({
        ...template,
        id: undefined,
        name: `${template.name} (Copy)`,
      });
      setFormModalOpened(true);
    },
    [setSelectedTemplate]
  );

  // Handle view/preview template
  // const handleViewTemplate = useCallback((template: EmbeddingEventTemplate) => {
  //   // TODO: Implement template preview functionality
  //   console.log("Preview template:", template);
  // }, []);

  // Handle remove template button click
  const handleRemoveTemplateClick = useCallback(
    (template: EmbeddingEventTemplate) => {
      setTemplateToDelete(template);
    },
    []
  );

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (templateToDelete?.id) {
      const success = await remove(templateToDelete.id);
      if (success) {
        setTemplateToDelete(null);
      }
    }
  }, [templateToDelete, remove]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setTemplateToDelete(null);
  }, []);

  // Handle form modal close
  const handleFormModalClose = useCallback(() => {
    setFormModalOpened(false);
    setSelectedTemplate(null);
    clearError();
  }, [setSelectedTemplate, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle template creation
  const handleCreateTemplate = useCallback(
    async (templateData: any) => {
      const result = await create(templateData);
      if (result) {
        setFormModalOpened(false);
        setSelectedTemplate(null);
      }
      return result;
    },
    [create, setSelectedTemplate]
  );

  // Handle template update
  const handleUpdateTemplate = useCallback(
    async (templateId: string, templateData: any) => {
      const result = await update(templateId, templateData);
      if (result) {
        setFormModalOpened(false);
        setSelectedTemplate(null);
      }
      return result;
    },
    [update, setSelectedTemplate]
  );

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Page toolbar with actions and search */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "actions",
              items: [
                <AppButton
                  key="add-template"
                  iconName="Plus"
                  onClick={handleAddTemplate}
                  disabled={isCreating || !!removingTemplateId || loading}
                >
                  Add
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={isCreating || !!removingTemplateId || loading}
                >
                  Refresh
                </AppButton>,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "search",
              items: [
                <SearchInput
                  key="search"
                  placeholder="Search templates..."
                  value={searchValue}
                  onChange={(value) => handleSearchChange(value)}
                  style={{ minWidth: 250 }}
                />,
              ],
              align: "right",
            },
          ]}
        />

        {/* Templates table */}
        <EmbeddingEventTemplatesTable
          templates={embeddingEventTemplates}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onLoadMore={loadMore}
          onEditTemplate={handleEditTemplate}
          onDuplicateTemplate={handleDuplicateTemplate}
          onRemoveTemplate={handleRemoveTemplateClick}
          //onViewTemplate={handleViewTemplate}
          removingTemplateId={removingTemplateId}
          editedTemplateId={embeddingEventTemplate?.id}
        />

        {/* Empty state when no templates */}
        <EmptyState
          display={isEmpty && !loading}
          title="No templates yet"
          description="Start by creating your first embedding event template"
          actionIcon="Plus"
          actionLabel="Add Template"
          onAction={handleAddTemplate}
        />
      </Stack>

      {/* Template form modal for create/edit */}
      <EmbeddingEventTemplateFormModal
        opened={formModalOpened}
        onClose={handleFormModalClose}
        template={selectedTemplate}
        onCreateTemplate={handleCreateTemplate}
        onUpdateTemplate={handleUpdateTemplate}
        isCreating={isCreating}
        isUpdating={isUpdating}
        lastError={lastError}
        onClearError={clearError}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={!!templateToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Template"
        message={`Are you sure you want to delete "${templateToDelete?.name}"?`}
        details="This action cannot be undone. The template will be permanently removed."
        confirmInProgress={removingTemplateId === templateToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
